import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Cpu, Globe, Lock, Shield, Users } from 'lucide-react';
import { Header } from '../components/ToxicDetector/Header';
import { Footer } from '../components/ToxicDetector/Footer';
import { MetaTags } from '../components/SEO/MetaTags';
import { ABOUT_SCHEMA, ABOUT_SEO } from '../utils/aboutSeo';
export function AboutPage() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <>
      <MetaTags title={ABOUT_SEO.title} description={ABOUT_SEO.description} keywords={ABOUT_SEO.keywords} canonical={ABOUT_SEO.canonical} ogTitle={ABOUT_SEO.title} ogDescription={ABOUT_SEO.description} ogImage={ABOUT_SEO.image} ogType="website" schema={ABOUT_SCHEMA} data-id="element-3303" data-mp="1gkz1axpbzwdp" />

      <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white selection:bg-[#FF0000] selection:text-white" data-id="element-3304" data-mp="2jw3l1lnl12k9">
        <Header data-id="element-3305" data-mp="25xm3y6s6mobb" />

        <main className="flex-grow relative overflow-hidden" data-id="element-3306" data-mp="2aum9mgl4dp1w">
          {/* Background Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-20 pointer-events-none" data-id="element-3307" data-mp="2yk6s6ydd2kan" />

          <div className="max-w-6xl mx-auto px-6 py-20 relative z-10" data-id="element-3308" data-mp="2oux2fp5zpdpp">
            {/* Hero Section */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={containerVariants} className="mb-32 text-center md:text-left" data-id="element-3309" data-mp="2tsormbf92lq7">
              <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF0000]/30 bg-[#FF0000]/5 text-[#FF0000] text-xs font-mono uppercase tracking-widest mb-6" data-id="element-3310" data-mp="3s5e0ws9t1">
                <span className="w-2 h-2 rounded-full bg-[#FF0000] animate-pulse" data-id="element-3311" data-mp="3m9oh0xx8iz48" />
                System Status: Operational
              </motion.div>

              <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-black tracking-tighter uppercase mb-6 leading-none" data-id="element-3312" data-mp="3d7ysbzuqmea5">
                Decoding the{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500" data-id="element-3313" data-mp="33hlx9dpar2pr">
                  Invisible
                </span>
                .
              </motion.h1>

              <motion.p variants={itemVariants} className="text-xl md:text-2xl text-gray-400 font-mono max-w-3xl border-l-2 border-[#FF0000] pl-6 ml-1" data-id="element-3314" data-mp="2tieluw24hi5a">
                We don't just read messages; we map the architecture of
                manipulation.
              </motion.p>
            </motion.section>

            {/* Mission & Methodology Grid */}
            <section className="grid md:grid-cols-2 gap-12 mb-32" data-id="element-3315" data-mp="1e0i07rklrjnd">
              {/* Mission */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="relative group" data-id="element-3316" data-mp="1iyjl8i1l9t1h">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-[#FF0000]/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-500 blur rounded-lg" data-id="element-3317" data-mp="2l37x13c9fyl" />
                <div className="relative h-full bg-[#0A0A0A] border border-gray-800 p-8 rounded-lg" data-id="element-3318" data-mp="7hjv9t2prqam">
                  <div className="flex items-center gap-3 mb-6" data-id="element-3319" data-mp="257gv5aagcmd7">
                    <Activity className="text-[#FF0000]" size={24} data-id="element-3320" data-mp="2591mkr1gzp6u" />
                    <h2 className="text-xl font-bold uppercase tracking-widest" data-id="element-3321" data-mp="2iz58ti0cniwa">
                      The Mission
                    </h2>
                  </div>
                  <p className="text-gray-400 leading-relaxed mb-6" data-id="element-3322" data-mp="2sx1fnxm97rol">
                    ToxicDetector was born from a singular realization: The
                    human nervous system is no match for modern psychological
                    warfare. While general AI is programmed to be "polite," our
                    Forensic Engine is programmed for the truth.
                  </p>
                  <p className="text-white font-medium border-t border-gray-800 pt-6" data-id="element-3323" data-mp="2wwtrcj6y2dy6">
                    We provide the objective data needed to break the cycle of
                    cognitive dissonance.
                  </p>
                </div>
              </motion.div>

              {/* Methodology */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="relative group" data-id="element-3324" data-mp="3j723v7xio8zf">
                <div className="absolute -inset-0.5 bg-gradient-to-l from-[#FF0000]/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-500 blur rounded-lg" data-id="element-3325" data-mp="30hddzt8iviqx" />
                <div className="relative h-full bg-[#0A0A0A] border border-gray-800 p-8 rounded-lg" data-id="element-3326" data-mp="2qhmlfx3ivdth">
                  <div className="flex items-center gap-3 mb-6" data-id="element-3327" data-mp="kqhfrhiek42m">
                    <Cpu className="text-[#FF0000]" size={24} data-id="element-3328" data-mp="or19eb3ropk0" />
                    <h2 className="text-xl font-bold uppercase tracking-widest" data-id="element-3329" data-mp="oq8d642fpig4">
                      The Methodology
                    </h2>
                  </div>
                  <p className="text-gray-400 leading-relaxed mb-6" data-id="element-3330" data-mp="1sntxk76gbuoy">
                    Our proprietary{' '}
                    <span className="text-[#FF0000] font-mono" data-id="element-3331" data-mp="1oo2ylg6ru89c">
                      V.2.4.0 Forensic Engine
                    </span>{' '}
                    was developed at the intersection of Computational
                    Linguistics and Behavioral Psychology.
                  </p>
                  <p className="text-gray-400 leading-relaxed" data-id="element-3332" data-mp="2cdxbtyv05yu4">
                    By analyzing thousands of data points—from DARVO patterns to
                    intermittent reinforcement cycles—we provide a "sanity
                    check" backed by data, not just intuition.
                  </p>
                </div>
              </motion.div>
            </section>

            {/* Leadership Section */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={containerVariants} className="mb-32" data-id="element-3333" data-mp="2va5jtafegnt4">
              <div className="flex items-center gap-4 mb-12" data-id="element-3334" data-mp="3claraepzscme">
                <div className="h-px flex-grow bg-gray-800" data-id="element-3335" data-mp="3cn5fc1wk8kup" />
                <h2 className="text-2xl font-black uppercase tracking-widest text-center" data-id="element-3336" data-mp="3gnp9qxpr1ad5">
                  The Architecture
                </h2>
                <div className="h-px flex-grow bg-gray-800" data-id="element-3337" data-mp="3ljwiszdfz1zb" />
              </div>

              <div className="grid lg:grid-cols-3 gap-8" data-id="element-3338" data-mp="3qizfnwj9q1w7">
                {/* Founder Card */}
                <motion.div variants={itemVariants} className="lg:col-span-2 bg-[#0A0A0A] border border-gray-800 rounded-lg p-8 relative overflow-hidden group" data-id="element-3339" data-mp="3vcc0rwoqnqk7">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-3340" data-mp="84rljbiqul0o">
                    <Shield size={120} data-id="element-3341" data-mp="ygvvj13c2qnt" />
                  </div>
                  <div className="relative z-10" data-id="element-3342" data-mp="yf1a7lo5gwj7">
                    <div className="inline-block px-3 py-1 bg-[#FF0000] text-white text-xs font-bold uppercase tracking-wider mb-4" data-id="element-3343" data-mp="tiauizg9c2yb">
                      Leadership
                    </div>
                    <h3 className="text-3xl font-bold mb-2" data-id="element-3344" data-mp="13g8kbrdrm7t1">
                      Chukwuka Paulgreen Agodi
                    </h3>
                    <p className="text-[#FF0000] font-mono text-sm uppercase tracking-widest mb-6" data-id="element-3345" data-mp="fr632kn1mgz2">
                      Founder & Chief Architect
                    </p>
                    <p className="text-gray-400 leading-relaxed max-w-2xl" data-id="element-3346" data-mp="pgpn3tmhsgvi">
                      ToxicDetector was created by Nigerian AI innovator
                      Chukwuka Paulgreen Agodi, who focuses on combining
                      behavioral psychology and artificial intelligence to
                      detect manipulation patterns in digital conversations. His
                      vision drives the core philosophy of the platform: making
                      the invisible mechanics of manipulation visible to
                      everyone.
                    </p>
                  </div>
                </motion.div>

                {/* Quantitative Team Card */}
                <motion.div variants={itemVariants} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-8 flex flex-col justify-center relative overflow-hidden" data-id="element-3347" data-mp="1yoswt8r3gcze">
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-[#FF0000]/5 rounded-full blur-2xl" data-id="element-3348" data-mp="2rdzw49ydh6lp" />
                  <Users className="text-[#FF0000] mb-4" size={32} data-id="element-3349" data-mp="2dcx7pqjwnk7m" />
                  <h3 className="text-xl font-bold mb-2 uppercase tracking-wide" data-id="element-3350" data-mp="2hfab48b6j618">
                    The Quantitative Team
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-3351" data-mp="11x8jkvux0f58">
                    Comprising experts in AES-256 encryption and NLP (Natural
                    Language Processing), our team ensures your data remains
                    anonymous while your results remain surgical.
                  </p>
                </motion.div>
              </div>
            </motion.section>

            {/* Metrics Section */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={containerVariants} className="border-t border-gray-800 pt-20" data-id="element-3352" data-mp="1htfuhzjfhuxz">
              <div className="grid md:grid-cols-3 gap-8" data-id="element-3353" data-mp="1cxh280uzbssa">
                {/* Valuation */}
                <motion.div variants={itemVariants} className="text-center md:text-left" data-id="element-3354" data-mp="yxz9xbih5xe6">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2 text-gray-500 text-xs font-mono uppercase tracking-widest" data-id="element-3355" data-mp="2tap1hmldeuie">
                    <Activity size={14} data-id="element-3356" data-mp="2xb7w1oeneeoa" />
                    <span data-id="element-3357" data-mp="loe4k3iyvx3r">Ecosystem Valuation</span>
                  </div>
                  <div className="text-4xl font-black text-white mb-1" data-id="element-3358" data-mp="lmksu9thymow">
                    $3M USD
                  </div>
                  <div className="text-[#FF0000] text-sm font-bold uppercase" data-id="element-3359" data-mp="ve7589hkmrt8">
                    Seed Stage
                  </div>
                </motion.div>

                {/* Data Integrity */}
                <motion.div variants={itemVariants} className="text-center md:text-left" data-id="element-3360" data-mp="15cejgkkh6vrj">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2 text-gray-500 text-xs font-mono uppercase tracking-widest" data-id="element-3361" data-mp="10d1vtxls5knw">
                    <Lock size={14} data-id="element-3362" data-mp="19ehyfj6csihk" />
                    <span data-id="element-3363" data-mp="19fjgw8pp879b">Data Integrity</span>
                  </div>
                  <div className="text-4xl font-black text-white mb-1" data-id="element-3364" data-mp="3g46bmeiywstq">
                    Zero-Knowledge
                  </div>
                  <div className="text-gray-400 text-sm" data-id="element-3365" data-mp="3b762tvuqhbyx">Architecture</div>
                </motion.div>

                {/* Global Reach */}
                <motion.div variants={itemVariants} className="text-center md:text-left" data-id="element-3366" data-mp="1k1jo8horhv26">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2 text-gray-500 text-xs font-mono uppercase tracking-widest" data-id="element-3367" data-mp="1ozl9985r04ga">
                    <Globe size={14} data-id="element-3368" data-mp="1y1c9hxhirj2r" />
                    <span data-id="element-3369" data-mp="1y0b0rznz78o7">Global Reach</span>
                  </div>
                  <div className="text-4xl font-black text-white mb-1" data-id="element-3370" data-mp="2305ra23u9w8q">
                    500+ Scans
                  </div>
                  <div className="text-gray-400 text-sm" data-id="element-3371" data-mp="27s6wx56i4ops">
                    Hourly across 12 countries
                  </div>
                </motion.div>
              </div>
            </motion.section>

            {/* Final CTA — back to scanner */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={containerVariants} className="mt-32 mb-16" data-id="element-3372" data-mp="2gpzeyh3lyagv">
              <motion.div variants={itemVariants} className="relative bg-[#0a0a0a] border border-gray-800 p-10 md:p-14 overflow-hidden" data-id="element-3373" data-mp="53p2o567036o">
                {/* Corner brackets */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#FF0000]" data-id="element-3374" data-mp="3sb9eshgw4slz" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#FF0000]" data-id="element-3375" data-mp="svvx2yscy03g" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#FF0000]" data-id="element-3376" data-mp="outxuhyfxo1h" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#FF0000]" data-id="element-3377" data-mp="f2o3j1kpuuj4" />

                {/* Subtle scan line */}
                <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF0000]/60 to-transparent" data-id="element-3378" data-mp="1citw02y30gq3" />

                <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8" data-id="element-3379" data-mp="17ph7dstek9xc">
                  <div className="flex-1" data-id="element-3380" data-mp="wanrfy8qithq">
                    <div className="inline-flex items-center gap-2 mb-4 text-[#FF0000] text-xs font-mono uppercase tracking-widest" data-id="element-3381" data-mp="wbp241lu9dyx">
                      <span className="w-2 h-2 rounded-full bg-[#FF0000] animate-pulse" data-id="element-3382" data-mp="idh9u6zqz0u3" />
                      Ready to Decode
                    </div>
                    <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight uppercase mb-3 leading-tight" data-id="element-3383" data-mp="1b0v05m8xwy58">
                      Now run it on a real conversation.
                    </h2>
                    <p className="text-gray-400 font-mono text-sm md:text-base max-w-xl" data-id="element-3384" data-mp="1197czxjf239k">
                      You've seen the architecture. Drop in a chat and watch the
                      patterns surface in seconds.
                    </p>
                  </div>

                  <a href="/upload" className="group flex-shrink-0 inline-flex items-center justify-center gap-3 bg-[#FF0000] text-white px-8 py-4 font-black uppercase tracking-widest text-sm shadow-[0_0_25px_rgba(255,0,0,0.35)] hover:bg-red-600 hover:shadow-[0_0_35px_rgba(255,0,0,0.55)] transition-all" data-id="element-3385" data-mp="1ypwefq4gf3if">
                    <Activity className="w-5 h-5" data-id="element-3386" data-mp="2hvu4xmcknu3j" />
                    <span data-id="element-3387" data-mp="2cvy1bo7se4oi">Run a Scan</span>
                    <span className="transition-transform group-hover:translate-x-1" data-id="element-3388" data-mp="2cwzkyh2vbzhu">
                      →
                    </span>
                  </a>
                </div>
              </motion.div>
            </motion.section>

            {/* Corporate Attribution */}
            <div className="mt-12 text-center" data-id="element-3389" data-mp="1z52xiqc5adrs">
              <span className="text-xs font-mono uppercase tracking-widest text-gray-500" data-id="element-3390" data-mp="1u4onj2qzgtr4">
                A product of ToxicDetector AI & Forensic Systems LTD.
              </span>
            </div>
          </div>
        </main>

        <Footer data-id="element-3391" data-mp="35iisankcoerj" />
      </div>
    </>;
}